import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import 'leaflet.heat';

// Fix default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const HotspotMap = ({
  hotspots: initialHotspots = [],
  tripLocations = [],
  trips = [],
  statusFilter = 'ALL',
  height = '400px',
  fetchUrl = null,
  onHotspotsLoaded = () => {},
  mapMode = 'cluster',
  isGrouped = false
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);
  const clusterGroupRef = useRef(null);
  const heatLayerRef = useRef(null);
  const [hotspots, setHotspots] = useState(initialHotspots);
  const mapStateRef = useRef({ zoom: null, center: null });

  // fetch from backend if a URL is provided and no data was passed
  useEffect(() => {
    if (!fetchUrl) return;
    if (initialHotspots.length > 0) return; // parent already supplied data

    let cancelled = false;

    const load = async () => {
      try {
        const res = await fetch(fetchUrl);
        if (!res.ok) throw new Error('Network response was not ok');
        const rsp = await res.json();
        const list = (rsp && rsp.data && Array.isArray(rsp.data.locations))
          ? rsp.data.locations
          : [];
        if (!cancelled) {
          setHotspots(list);
          onHotspotsLoaded(list);
        }
      } catch (err) {
        console.error('Failed to load hotspots:', err);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [fetchUrl, initialHotspots, onHotspotsLoaded]);

  // map initialization (same pattern as TripMap)
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    const map = L.map(mapRef.current).setView([11.05, 77.0], 6);
    mapInstanceRef.current = map;

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
      subdomains: ['a', 'b', 'c']
    }).addTo(map);

    markersLayerRef.current = L.layerGroup().addTo(map);

    clusterGroupRef.current = L.markerClusterGroup({
      chunkedLoading: true,
      chunkInterval: 200,
      chunkDelay: 50,
      maxClusterRadius: 60,
      disableClusteringAtZoom: 17,
      spiderfyOnMaxZoom: true,
      removeOutsideVisibleBounds: true,
    });
    map.addLayer(clusterGroupRef.current);

    map.on('zoomend moveend', () => {
      mapStateRef.current = {
        zoom: map.getZoom(),
        center: map.getCenter()
      };
    });

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // whenever hotspots list updates, rebuild markers & adjust bounds
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !markersLayerRef.current || !clusterGroupRef.current) return;

    markersLayerRef.current.clearLayers();
    clusterGroupRef.current.clearLayers();
    if (heatLayerRef.current) {
      map.removeLayer(heatLayerRef.current);
      heatLayerRef.current = null;
    }

    // Helper to create icon
    const createCustomIcon = (color, size = 20) => {
      return L.divIcon({
        html: `<div style="background-color: ${color}; width: ${size}px; height: ${size}px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
        className: 'custom-marker',
        iconSize: [size, size],
        iconAnchor: [Math.floor(size/2), Math.floor(size/2)]
      });
    };

    const coords = [];
    const heatData = [];

    // Add hotspot markers
    hotspots.forEach((hotspot) => {
      if (!hotspot.location) return;
      
      let lng, lat;
      if (Array.isArray(hotspot.location)) {
        [lng, lat] = hotspot.location;
      } else if (typeof hotspot.location === 'object') {
        lng = hotspot.location.lng || hotspot.location.lon || hotspot.location.longitude;
        lat = hotspot.location.lat || hotspot.location.latitude;
      }
      
      if (typeof lng !== 'number' || typeof lat !== 'number') return;

      const cancelRate = hotspot.totalTrips
        ? (hotspot.cancelledTrips || 0) / hotspot.totalTrips
        : 0;
      const color = cancelRate > 0.5 ? '#EF4444' : '#10B981';
      
      const popup = `<div class="text-center" style="min-width:120px;"><div class="font-bold mb-1">${hotspot.name || hotspot.location.address || 'Location'}</div><div class="text-sm">Total: ${hotspot.totalTrips || 0}</div><div class="text-sm text-green-600">✔️ ${hotspot.completedTrips || 0}</div><div class="text-sm text-red-600">❌ ${hotspot.cancelledTrips || 0}</div><div class="text-sm text-yellow-600">⚠️ ${hotspot.divergedTrips || 0}</div></div>`;
      
      let marker;
      if (mapMode === 'pins') {
        const dotIcon = L.divIcon({
          html: `<div style="background-color: ${color}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
          className: 'custom-marker',
          iconSize: [12, 12],
          iconAnchor: [6, 6]
        });
        marker = L.marker([lat, lng], { icon: dotIcon });
        marker.bindPopup(popup);
        markersLayerRef.current.addLayer(marker);
      } else if (mapMode === 'cluster') {
        marker = L.marker([lat, lng], { icon: createCustomIcon(color, 24) });
        marker.bindPopup(popup);
        clusterGroupRef.current.addLayer(marker);
      }
      
      coords.push([lat, lng]);
      heatData.push([lat, lng, hotspot.totalTrips || 1]);
    });

    // Add trip location pins
    trips.forEach((trip) => {
      if (!trip.location || !Array.isArray(trip.location) || trip.location.length < 2) return;

      const [lat, lon] = trip.location;
      if (typeof lat !== 'number' || typeof lon !== 'number') return;

      const popup = `<b>Trip ID:</b> ${trip.tripId}`;
      
      let marker;
      if (mapMode === 'pins') {
        const dotIcon = L.divIcon({
          html: `<div style="background-color: #3B82F6; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
          className: 'custom-marker',
          iconSize: [12, 12],
          iconAnchor: [6, 6]
        });
        marker = L.marker([lat, lon], { icon: dotIcon });
        marker.bindPopup(popup);
        markersLayerRef.current.addLayer(marker);
      } else if (mapMode === 'cluster') {
        marker = L.marker([lat, lon]);
        marker.bindPopup(popup);
        clusterGroupRef.current.addLayer(marker);
      }
      
      coords.push([lat, lon]);
      heatData.push([lat, lon, 1]);
    });

    // Add trip location pins from API
    tripLocations.forEach((trip) => {
      if (!trip.location || !Array.isArray(trip.location) || trip.location.length < 2) return;
      
      let match = false;
      if (statusFilter === 'ALL') {
        match = true;
      } else if (statusFilter === 'BOOKING') {
        match = true;
      } else if (statusFilter === 'BOOKING_COMPLETED') {
        match = trip.status === 'COMPLETED';
      } else if (statusFilter === 'BOOKING_CANCELLED') {
        match = trip.status && /CANCELLED/i.test(trip.status);
      } else if (statusFilter === 'CANCELLED') {
        match = trip.status && /CANCELLED/i.test(trip.status);
      } else {
        match = trip.status === statusFilter;
      }
      
      if (!match) return;

      const [lng, lat] = trip.location;
      if (typeof lat !== 'number' || typeof lng !== 'number') return;

      // Prepare popup content with grouping info
      let popupContent = `<div class="text-sm"><b>Trip ID:</b> ${trip.tripId}<br/><b>Status:</b> ${trip.status || 'N/A'}`;
      
      if (isGrouped && trip.isGrouped && trip.attempts > 1) {
        popupContent += `<br/><b class="text-orange-600">Grouped Attempts:</b> ${trip.attempts}`;
        if (trip.groupedTrips && trip.groupedTrips.length > 0) {
          popupContent += `<br/><span class="text-xs text-gray-600">Trip IDs: ${trip.groupedTrips.slice(0, 5).join(', ')}${trip.groupedTrips.length > 5 ? '...' : ''}</span>`;
        }
      }
      popupContent += '</div>';
      
      // Determine marker color and size based on grouping
      let markerColor = '#8B5CF6';
      let markerSize = 12;
      
      if (isGrouped && trip.isGrouped && trip.attempts > 1) {
        markerColor = '#F59E0B'; // Orange for grouped trips
        markerSize = Math.min(20, 12 + trip.attempts * 2); // Scale size with attempts
      }
      
      if (mapMode === 'pins') {
        const dotIcon = L.divIcon({
          html: `<div style="background-color: ${markerColor}; width: ${markerSize}px; height: ${markerSize}px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
          className: 'custom-marker',
          iconSize: [markerSize, markerSize],
          iconAnchor: [markerSize/2, markerSize/2]
        });
        const marker = L.marker([lat, lng], { icon: dotIcon });
        marker.bindPopup(popupContent);
        markersLayerRef.current.addLayer(marker);
      } else if (mapMode === 'cluster') {
        const clusterIcon = L.divIcon({
          html: `<div style="background-color: ${markerColor}; width: ${markerSize}px; height: ${markerSize}px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
          className: 'custom-marker',
          iconSize: [markerSize, markerSize],
          iconAnchor: [markerSize/2, markerSize/2]
        });
        const marker = L.marker([lat, lng], { icon: clusterIcon });
        marker.bindPopup(popupContent);
        clusterGroupRef.current.addLayer(marker);
      }
      
      coords.push([lat, lng]);
      const weight = isGrouped && trip.attempts ? trip.attempts : 1;
      heatData.push([lat, lng, weight]);
    });

    // HeatMap mode
    if (mapMode === 'heatmap' && heatData.length > 0) {
      // Calculate a reasonable max value to prevent single trips from being too bright
      // Use the 90th percentile or a minimum threshold to show actual density
      const weights = heatData.map(d => d[2]).sort((a, b) => b - a);
      const maxWeight = Math.max(...weights);
      
      // Set max to at least 5 to prevent single trips from showing as hotspots
      // If max weight is higher, use that to show true density
      const effectiveMax = Math.max(5, maxWeight * 0.5);
      
      heatLayerRef.current = L.heatLayer(heatData, {
        radius: 25,           // Smaller radius for more precise hotspots
        blur: 15,             // Less blur for clearer visualization
        maxZoom: 18,          // Show heatmap at higher zoom levels
        minOpacity: 0.3,      // Minimum opacity so dim points are still visible
        max: effectiveMax,    // Normalized max to show true density
        gradient: {
          0.0: '#3b82f6',     // Blue (low density - 1-2 trips)
          0.4: '#10b981',     // Green (moderate density)
          0.6: '#fbbf24',     // Yellow (medium-high density)
          0.8: '#f97316',     // Orange (high density)
          1.0: '#ef4444'      // Red (very high density/hotspot)
        }
      }).addTo(map);
    }

    if (coords.length > 0) {
      if (mapStateRef.current.zoom && mapStateRef.current.center) {
        map.setView(mapStateRef.current.center, mapStateRef.current.zoom);
      } else if (coords.length === 1) {
        map.setView(coords[0], 11);
      } else {
        map.fitBounds(L.latLngBounds(coords), { padding: [50, 50], maxZoom: 16 });
      }
    }
  }, [hotspots, tripLocations, trips, statusFilter, mapMode, isGrouped]);

  return (
    <div style={{ height, width: '100%' }}>
      <div className="relative bg-gray-200 rounded-lg overflow-hidden" style={{ height }}>
        <div ref={mapRef} style={{ height: '100%', width: '100%' }}></div>
      </div>
    </div>
  );
};

export default HotspotMap;
